import { useApolloClient } from "@apollo/client";
import { Modal } from "@msys/ui";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  IntegrationId,
  namedOperations,
} from "../../../../clients/graphqlTypes.js";
import { PasswordField } from "../../../commons/form-fields/PasswordField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { getIntegrationMeta } from "../integrationRegistry.js";
import { integrationCreatePath, integrationSettingsPath } from "../paths.js";
import { integrationErrorMessage } from "../integrationErrorMessage.js";
import { useIntegrationSettings_AddMutation } from "./integration-settings/IntegrationSettings.generated.js";

interface FormValues {
  baseUrl: string;
  accessToken: string;
  foreignAccountId: string;
}

interface Props {
  integrationId: string;
  integrationName: string;
  isForeignAccountIdRequired: boolean;
  handleClose: () => void;
}

/**
 * Connect flow for the overview cards (design review 2026-07-14): a simple
 * modal with the integration's create fields, following the pattern of the
 * former Data & Integrations box. Integrations flagged `connectViaPage`
 * (sap_s4hana) show no fields here — their create form is too large for a
 * modal, so the primary action continues to the create-mode settings page.
 */
export const ConnectIntegrationModal = ({
  integrationId,
  integrationName,
  isForeignAccountIdRequired,
  handleClose,
}: Props) => {
  const { t } = useTranslate(["Integrations", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const client = useApolloClient();
  const formId = React.useMemo(() => uniqueId(), []);
  const meta = getIntegrationMeta(integrationId);
  const isPdsSync = integrationId === "pds_sync";
  const connectViaPage = !!meta.connectViaPage;

  const [addMutation] = useIntegrationSettings_AddMutation({
    client,
    context: { noErrorNotification: true },
  });

  const validationSchema = React.useMemo(() => {
    if (isPdsSync) {
      return Yup.object().shape({
        baseUrl: Yup.string()
          .url(t("Must be a valid URL"))
          .required(t("URL is required")),
        accessToken: Yup.string().required(t("Access token is required")),
      });
    }
    if (isForeignAccountIdRequired) {
      return Yup.object().shape({
        foreignAccountId: Yup.string().required(),
      });
    }
    return Yup.object().shape({});
  }, [isPdsSync, isForeignAccountIdRequired, t]);

  return (
    <Formik<FormValues>
      initialValues={{ baseUrl: "", accessToken: "", foreignAccountId: "" }}
      validationSchema={validationSchema}
      onSubmit={async values => {
        if (connectViaPage) {
          handleClose();
          navigate(integrationCreatePath(integrationId));
          return;
        }
        try {
          await addMutation({
            variables: {
              input: {
                integrationId: integrationId as IntegrationId,
                // null (not {}) — plugins apply their create defaults only
                // for a missing settings object; {} is truthy and fails
                // schemas with required fields
                settings: isPdsSync
                  ? {
                      baseUrl: values.baseUrl,
                      accessToken: values.accessToken,
                    }
                  : null,
                foreignAccountId: isForeignAccountIdRequired
                  ? values.foreignAccountId
                  : null,
              },
            },
            refetchQueries: [
              namedOperations.Query.IntegrationsOverview,
              namedOperations.Query.OrganisationIntegrationsSidebar,
            ],
            awaitRefetchQueries: true,
          });
          handleClose();
          if (isPdsSync) {
            // Land in the PDS settings page after connecting (design review)
            // — not the top-level Tasks page.
            navigate(integrationSettingsPath("pds_sync"));
          }
        } catch (e) {
          enqueueSnackbar(
            integrationErrorMessage(
              e,
              t("Something went wrong. Please try again.", { ns: "Global" })
            ),
            { variant: "error" }
          );
        }
      }}
    >
      {formikProps => (
        <Modal
          handleClose={handleClose}
          dialogProps={{ maxWidth: "xs" }}
          title={t("Connect {name}", {
            name: integrationName,
            ns: "Integrations",
          })}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: connectViaPage
                ? t("Continue", { ns: "Global" })
                : t("Connect"),
              buttonProps: {
                type: "submit",
                form: formId,
                loading: formikProps.isSubmitting,
                disabled: formikProps.isSubmitting,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t(meta.description as any)}
              </Typography>

              {isPdsSync && (
                <>
                  <TextField
                    name="baseUrl"
                    label={t("PDS Server URL")}
                    placeholder="https://pds.example.com"
                    required
                    disabled={formikProps.isSubmitting}
                  />
                  <PasswordField name="accessToken" label={t("Access token")} />
                  <Typography variant="caption" color="text.secondary">
                    {t(
                      "You will receive the URL and access token from your PDS contact or administrator."
                    )}
                  </Typography>
                </>
              )}

              {!isPdsSync && !connectViaPage && isForeignAccountIdRequired && (
                <TextField
                  name="foreignAccountId"
                  label={t("Account ID")}
                  required
                  disabled={formikProps.isSubmitting}
                />
              )}

              {connectViaPage && (
                <Typography variant="caption" color="text.secondary">
                  {t("You will configure the connection in the next step.")}
                </Typography>
              )}

              {/* Plain one-click connect (no upfront inputs) — make clear
                  nothing must be filled in now */}
              {!isPdsSync && !connectViaPage && !isForeignAccountIdRequired && (
                <Typography variant="caption" color="text.secondary">
                  {t("All settings can be configured after connecting.")}
                </Typography>
              )}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
